import { inject, Injectable, Injector, signal } from '@angular/core';
import { Messaging, getToken, deleteToken, onMessage } from '@angular/fire/messaging';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from './toastr.service';
import { RefreshersService } from './refreshers.service';

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  baseUrl = environment.apiUrl;
  http = inject(HttpClient);
  refreshers = inject(RefreshersService);
  injector = inject(Injector);
  private messaging = inject(Messaging);
  FCM_TOKEN_KEY = 'ft';
  // TODO: set your Web Push (VAPID) public key from Firebase Console > Project Settings > Cloud Messaging
  private vapidKey: string | undefined = (environment.firebase as any)?.vapidKey;


  fcmToken = signal<string | null>(null);

  constructor() {
    this.checkPermission()
    this.listenForBackgroundMessages()
    this.listenForMessages()
  }

  async checkPermission() {
    const permission = Notification.permission;
    console.log('Notification permission:', permission);

    if (permission === 'granted') {
      return;
    }

    if (permission === 'denied') {
      console.warn('Notification permission is denied. Enable notifications from browser site settings.');
      return;
    }

    try {
      const result = await Notification.requestPermission();
      if (result === 'granted') {
        console.log('Notification permission granted');
      } else {
        console.warn('Notification permission was not granted:', result);
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error);
    }
  }

  async getFcmToken() {
    this.fcmToken.set(localStorage.getItem(this.FCM_TOKEN_KEY));
    try {
      const swRegistration = 'serviceWorker' in navigator
        ? await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')
          ?? await navigator.serviceWorker.register('/firebase-messaging-sw.js')
        : undefined;

      const token = await getToken(this.messaging, {
        vapidKey: this.vapidKey,
        serviceWorkerRegistration: swRegistration,
      });

      if (token) {
        if (token !== this.fcmToken()) {
          this.updateFcmToken(token);
        }
      } else {
        console.warn('Firebase FCM token is null. Check notification permission, service worker registration, and Web Push certificate setup.');
        this.deleteToken();
      }
    } catch (error) {
      console.error('Failed to get Firebase FCM token:', error);
      this.deleteToken();
    }
  }

  listenForMessages(): Subscription {
    const sub = onMessage(this.messaging, (payload) => {
      this.handleNotification(payload, 'foreground');
    });
    return new Subscription(() => sub());
  }

  listenForBackgroundMessages() {
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker.addEventListener('message', (event) => {
      try {
        if (event.data?.type !== 'FCM_BACKGROUND_MESSAGE') return;
        this.handleNotification(event.data.payload, 'background');
      } catch (error) {
        console.error('Error handling background message:', error);
      }
    });
  }

  async deleteToken() {
    if (!this.fcmToken()) return;
    try {
      await deleteToken(this.messaging);
      console.log('Firebase FCM token deleted');
      this.fcmToken.set(null);
      localStorage.removeItem(this.FCM_TOKEN_KEY);
    } catch (error) {
      console.error('Failed to delete Firebase FCM token:', error);
    }
  }

  updateFcmToken(firebase_token: string) {
    localStorage.setItem(this.FCM_TOKEN_KEY, firebase_token);
    this.fcmToken.set(firebase_token);
    this.http.put(`${this.baseUrl}fcm-token`, { firebase_token }).subscribe({
      next: () => {
        console.log('Firebase FCM token updated');
      },
      error: (error) => {
        console.error('Failed to update Firebase FCM token:', error);
      }
    });
  }

  handleNotification(payload: any, type: 'foreground' | 'background') {
    // Handle notification here
    console.log(`Handling ${type} notification:`, payload);
    const toastr = this.injector.get(ToastrService);

    const data = payload.data
    if(['ticket_created', 'ticket_assigned'].includes(data?.notification_type)) {
      this.refreshers.clientsO.next(data);
      this.refreshers.ticketsO.next(data);
    }
    toastr.custom({
      severity: 'info',
      summary: data?.title || payload?.notification?.title,
      detail: data?.body || payload?.notification?.body,
      life: 5000
    });
  }
}

